import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useClutch } from "./ClutchContext";
import { API_KEY, SEND_OTP_URL, SIGN_IN_URL } from "../../utils/api-url-list";

const RESEND_SECONDS = 120;

// Demo bypass: skip the real OTP API for this number/code pair
const DEMO_PHONE = "0000000000";
const DEMO_OTP = "123456";

const Details = () => {
  const navigate = useNavigate();
  const { details, setDetails, login } = useClutch();
  const [form, setForm] = useState(details);
  const [otpStage, setOtpStage] = useState(false);
  const [otp, setOtp] = useState("");
  const [verified, setVerified] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const phoneDigits = (form.phone || "").replace(/\D/g, "");

  // 120s resend countdown
  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  const sendOtp = async () => {
    if (!/^\d{10}$/.test(phoneDigits)) { setErr("Enter a valid 10-digit mobile number"); return; }
    setErr("");
    // Demo bypass — no API call
    if (phoneDigits === DEMO_PHONE) {
      setOtpStage(true);
      setResendIn(RESEND_SECONDS);
      setOtp("");
      return;
    }
    setSending(true);
    try {
      const resp = await fetch(SEND_OTP_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          api_key: API_KEY,
          action: "send",
          channel_id: "login_mobile",
          phone_no: phoneDigits,
        }),
      });
      const json = await resp.json();
      if (json?.statusCode?.toString().startsWith("2")) {
        setOtpStage(true);
        setResendIn(RESEND_SECONDS);
        setOtp("");
      } else {
        throw new Error(json?.message || "Could not send OTP. Try again.");
      }
    } catch (e) {
      setErr(e.message || "Could not send OTP. Try again.");
    } finally {
      setSending(false);
    }
  };

  const verify = async () => {
    if (!/^\d{6}$/.test(otp)) { setErr("Enter the 6-digit code"); return; }
    setErr("");
    // Demo bypass — accept the fixed code without hitting the API
    if (phoneDigits === DEMO_PHONE) {
      if (otp === DEMO_OTP) {
        login({ id_token: "demo", refresh_token: "demo" });
        setVerified(true);
      } else {
        setErr("Incorrect code, try again");
      }
      return;
    }
    setVerifying(true);
    try {
      const resp = await fetch(SIGN_IN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          api_key: API_KEY,
          login_type: "auth",
          login_method: "otp_mobile",
          phone_no: phoneDigits,
          otp,
          referral_code: "",
        }),
      });
      const json = await resp.json();
      if (json?.statusCode?.toString().startsWith("2") && json?.user?.id_token && json?.user?.refresh_token) {
        login({ id_token: json.user.id_token, refresh_token: json.user.refresh_token });
        setVerified(true);
      } else {
        throw new Error(json?.message || "Incorrect code, try again");
      }
    } catch (e) {
      setErr(e.message || "Incorrect code, try again");
    } finally {
      setVerifying(false);
    }
  };

  const canContinue = form.name && form.age && form.gender && form.height && form.weight && verified;

  const proceed = () => {
    setDetails(form);
    navigate("/instructions");
  };

  return (
    <div className="cl-root">
      <div className="cl-bg" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/stadium-bg.png)` }} />
      <div className="cl-wrap cl-scroll">
        <div className="cl-row">
          <div className="cl-logo"><b>Care</b><span>Plix</span><sup>✛</sup></div>
          <div className="cl-badge">STEP 1 / 3</div>
        </div>

        <div style={{ marginTop: 22 }}>
          <div className="cl-kicker">YOUR DETAILS</div>
          <h1 className="cl-title" style={{ fontSize: "clamp(34px,10vw,46px)", marginTop: 8 }}>Line up<br />the shot.</h1>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 22 }}>
          <div className="cl-field">
            <label>Full name</label>
            <input value={form.name} onChange={set("name")} placeholder="Lionel M." autoComplete="off" />
          </div>
          <div className="cl-grid2">
            <div className="cl-field"><label>Age</label><input value={form.age} onChange={set("age")} placeholder="28" inputMode="numeric" /></div>
            <div className="cl-field">
              <label>Gender</label>
              <select value={form.gender} onChange={set("gender")}
                style={{ background: "rgba(8,14,24,.55)", border: "1px solid var(--cl-line)", borderRadius: 12, padding: 14, color: "#fff", fontFamily: "var(--cl-body)", fontSize: 16, outline: "none" }}>
                <option value="" disabled>Select</option>
                <option value="Male">Male</option><option value="Female">Female</option><option value="Other">Other</option>
              </select>
            </div>
          </div>
          <div className="cl-grid2">
            <div className="cl-field"><label>Height (cm)</label><input value={form.height} onChange={set("height")} placeholder="175" inputMode="numeric" /></div>
            <div className="cl-field"><label>Weight (kg)</label><input value={form.weight} onChange={set("weight")} placeholder="70" inputMode="numeric" /></div>
          </div>

          {!otpStage ? (
            <div className="cl-otprow">
              <div className="cl-field">
                <label>Mobile number</label>
                <input value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" inputMode="numeric" maxLength={14} />
              </div>
              <button className="cl-mini" onClick={sendOtp} disabled={sending}>{sending ? "Sending…" : "Send OTP"}</button>
            </div>
          ) : (
            <>
              <div className="cl-otprow">
                <div className="cl-field">
                  <label>Enter OTP <span style={{ color: "var(--cl-accent2)" }}>· sent to {phoneDigits}</span></label>
                  <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code" inputMode="numeric" maxLength={6} disabled={verified} />
                </div>
                <button className="cl-mini cl-mini-cta" onClick={verify} disabled={verified || verifying}>{verified ? "✓ Verified" : verifying ? "…" : "Verify"}</button>
              </div>
              {!verified && (
                <button
                  onClick={sendOtp}
                  disabled={resendIn > 0 || sending || verifying}
                  style={{ alignSelf: "flex-start", background: "none", border: "none", color: resendIn > 0 ? "rgba(255,255,255,.4)" : "var(--cl-accent2)", fontFamily: "var(--cl-mono)", fontSize: 12, cursor: resendIn > 0 ? "default" : "pointer", padding: 0 }}
                >
                  {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
                </button>
              )}
            </>
          )}

          {err && <p style={{ color: "var(--cl-coral)", fontSize: 12, fontFamily: "var(--cl-mono)" }}>{err}</p>}
        </div>

        <div className="cl-spacer" />
        <button className="cl-btn" onClick={proceed} disabled={!canContinue} style={{ marginTop: 18 }}>Continue</button>
        <p className="cl-foot" style={{ marginTop: 10 }}>Your details personalise your vitals reading</p>
      </div>
    </div>
  );
};

export default Details;
